import { useCallback } from 'react';

import type { GridApiPro } from '@packages/fenrir/components';
import {
  GRID_ACTIONS_COLUMN_TYPE,
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
  GRID_CHECKBOX_SELECTION_FIELD,
} from '@packages/fenrir/components';

interface UseExportColumnsProps {
  gridApiRef: React.RefObject<GridApiPro | null>;
  splApiNames: Record<string, string>;
  columnNamesMap: Record<string, string>;
  allColDefs: Record<string, string>;
}

// Fields that should be ignored during export
const IGNORED_FIELDS = [
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
  GRID_CHECKBOX_SELECTION_FIELD,
  GRID_ACTIONS_COLUMN_TYPE,
];

export const useExportColumns = ({
  gridApiRef,
  splApiNames,
  columnNamesMap,
  allColDefs,
}: UseExportColumnsProps) => {
  const getExportColumns = useCallback(() => {
    const visibleColumns = gridApiRef.current?.getVisibleColumns() || [];

    return visibleColumns.reduce<Record<string, string>>((acc, column) => {
      const fieldKey = column.field;

      if (IGNORED_FIELDS.includes(fieldKey)) {
        return acc;
      }

      const apiFieldName = splApiNames[fieldKey] || fieldKey;

      if (apiFieldName) {
        acc[apiFieldName] =
          columnNamesMap[apiFieldName as keyof typeof columnNamesMap] ||
          column.headerName ||
          String(column.field);
      }

      return acc;
    }, {});
  }, [gridApiRef, splApiNames, columnNamesMap]);

  return {
    getExportColumns,
  };
};
